import React from 'react';
import {useTranslations} from 'next-intl';
import Container from '@/container';
import {Grid, Row, Col} from '@/grid';
import {SectionPaddingHomeHero} from '@/section';
import {withMarginBottomLg, withMarginBottomZeroToLg} from '@/hocs/withSpacing';
import {withGreen800, withNeutral700, withNeutral800} from '@/hocs/withColors';
import {withHomeSubP, withHeadingEyebrow} from '@/hocs/withFontType';
import {H1, P} from '@/typography';
import Link from '@/links';
import ModalButton from '@/ui/modal-button';
import ModalWrapper from '@/ui/modal-wrapper';
import styles from './styles.module.scss';

const H1Green800 = withGreen800(H1);
const SubPNeutral800 = withNeutral800(withHomeSubP(P));
const EyebrowNeutral700 = withNeutral700(withHeadingEyebrow(P as never));
const DivMargined = withMarginBottomLg('div' as never);
const CtaDefaultMarginedMob = withMarginBottomZeroToLg(Link);

const VIDEO_PATH = '/static/videos/industry-construction-and-engineering';
const assetVersion = process.env.ASSET_VERSION || 'defaultVersion';

const FluidGrid = Grid as React.ComponentType<{
  fluid?: boolean;
  className?: string;
  tagName?: string;
  children?: React.ReactNode;
}>;

type ModalButtonProps = React.ComponentProps<typeof ModalButton> & {
  openName?: string;
  videoId: string;
  variant?: string;
  ariaLabel?: string;
  customAttributes?: Record<string, string>;
  transcript?: string;
};

const VideoModalButton = ModalButton as React.ForwardRefExoticComponent<
  ModalButtonProps & React.RefAttributes<HTMLButtonElement>
>;

const HeroSection = () => {
  const t = useTranslations('industryConstructionEngineering.hero');
  const sharedT = useTranslations('shared');

  const mobileMp4Src = `${VIDEO_PATH}/${t('previewVideoMobileUrl')}.mp4?v=${assetVersion}`;
  const desktopMp4Src = `${VIDEO_PATH}/${t('previewVideoDesktopUrl')}.mp4?v=${assetVersion}`;

  return (
    <SectionPaddingHomeHero>
      <Container variant="section-padding-below-nav">
        <FluidGrid fluid>
          <Row center="xs" start="lg" middle="lg">
            <Col xs={12} lg={6}>
              <EyebrowNeutral700>{t('eyebrow')}</EyebrowNeutral700>
              <H1Green800>{t('title')}</H1Green800>
              <SubPNeutral800>{t('subtitle')}</SubPNeutral800>
              <DivMargined>
                <ul className={styles['list-inline-to-bl']}>
                  <li>
                    <CtaDefaultMarginedMob
                      href="/free-trial"
                      variant="cta-default-xs"
                      customAttributes={{
                        'data-gtm': 'construction-hero-free-trial',
                        'data-ui': 'construction-hero-free-trial'
                      }}>
                      {sharedT('freeTrialCTALabel')}
                    </CtaDefaultMarginedMob>
                  </li>
                  <li>
                    <Link
                      href="/demo"
                      variant="primary"
                      customAttributes={{
                        'data-gtm': 'construction-hero-demo',
                        'data-ui': 'construction-hero-demo'
                      }}>
                      {sharedT('demoCTALabel')}
                    </Link>
                  </li>
                </ul>
              </DivMargined>
            </Col>

            <Col xs={12} lg={6}>
              <ModalWrapper variant="modal-btn-wrapper" className={styles['hero-modal']}>
                <video
                  className={styles['hero-preview-video']}
                  width={584}
                  height={624}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  aria-hidden="true">
                  <source media="(max-width: 991px)" src={mobileMp4Src} type="video/mp4" />
                  <source src={desktopMp4Src} type="video/mp4" />
                </video>
                <VideoModalButton
                  openName="constructionVideoModal"
                  videoId="py3xolt7qv"
                  variant="image-green"
                  ariaLabel={t('openVideoAriaLabel')}
                  customAttributes={{
                    'data-gtm': 'construction-hero-video'
                  }}
                  transcript={t('transcript')}
                />
              </ModalWrapper>
            </Col>
          </Row>
        </FluidGrid>
      </Container>
    </SectionPaddingHomeHero>
  );
};

export default HeroSection;
